import { useMemo, useState } from 'react'
import { EMILIA_PAGES } from '../config/emilia'
import { useAuth } from '../hooks/useAuth.js'

// Single nav tree for Sidebar (desktop rail) and MobileSidebar (drawer).
// Children are sub-pages of their parent — Home's children are card pages
// drawing from the same pool as Home itself. `adminOnly` entries are hidden from
// viewers (useNavTree); their API refuses viewers anyway.
export const NAV_TREE = [
  { id: 'my-dashboard', label: 'My Dashboard', icon: 'dashboard_customize' },
  {
    id: 'home', label: 'Home', icon: 'home',
    children: [
      { id: 'vehicles',   label: 'Vehicles',   icon: 'electric_car' },
      { id: 'appliances', label: 'Appliances', icon: 'wash' },
      { id: 'rooms',      label: 'Rooms',      icon: 'door_open' },
    ],
  },
  {
    id: 'energy', label: 'Energy', icon: 'wb_sunny',
    children: [
      { id: 'energy-flow',   label: 'Energiefluss', icon: 'account_tree' },
      { id: 'energy-wiring', label: 'Stromkreise',  icon: 'electrical_services' },
    ],
  },
  { id: 'house-map', label: 'House 3D', icon: 'view_in_ar' },
  {
    id: 'report', label: 'Report', icon: 'view_timeline',
    children: [
      { id: 'report-finance', label: 'Finanzen', icon: 'account_balance', adminOnly: true },
    ],
  },
  { id: 'security', label: 'Security', icon: 'shield' },
  {
    id: 'emilia', label: 'Emilia', icon: 'child_care',
    // `href` items open outside the app: the kids dashboard is its own full-screen page.
    children: [
      ...EMILIA_PAGES.map(p => ({ id: `emilia-${p.id}`, label: p.label, icon: p.icon })),
      { id: 'emilia-kids', label: 'Kids Dashboard', icon: 'toys', href: '/kids' },
    ],
  },
  // Desktop reaches Alerts through the header's center nav
  { id: 'notifications', label: 'Alerts', icon: 'notifications', mobileOnly: true },
  { id: 'logging', label: 'Automations', icon: 'article' },
  { id: 'systems', label: 'Systems', icon: 'developer_board' },
  {
    id: 'other-apps', label: 'Apps', icon: 'apps',
    children: [
      { id: 'extensions', label: 'Extensions', icon: 'extension' },
    ],
  },
  {
    id: 'docs', label: 'Docs', icon: 'menu_book',
    children: [
      { id: 'design-system', label: 'Design System', icon: 'palette' },
      { id: 'formulas', label: 'Formeln', icon: 'function' },
      { id: 'docs-hardware', label: 'Schaltpläne', icon: 'cable' },
      { id: 'docs-mcp', label: 'MCP Tools', icon: 'smart_toy' },
    ],
  },
]

/** NAV_TREE as the signed-in role may see it; a group left without children loses its fold. */
export function useNavTree() {
  const { user } = useAuth()
  const admin = user?.role === 'admin'
  return useMemo(() => (admin ? NAV_TREE : NAV_TREE.filter(i => !i.adminOnly).map(i => {
    if (!i.children) return i
    const children = i.children.filter(c => !c.adminOnly)
    return { ...i, children: children.length ? children : undefined }
  })), [admin])
}

/** Id of the group (parent with children) that contains `id`, parent or child. */
export const navGroupOf = (id) =>
  NAV_TREE.find(i => i.children && (i.id === id || i.children.some(c => c.id === id)))?.id

/**
 * Which nav groups are unfolded. The group holding the active page opens
 * itself on every navigation (URL, back button); the rest stay as toggled.
 */
export function useNavGroups(activeNav) {
  const activeGroup = navGroupOf(activeNav)
  const [openGroups, setOpenGroups] = useState(() => new Set(activeGroup ? [activeGroup] : []))

  const [seenNav, setSeenNav] = useState(activeNav)

  // Adjust during render (not in an effect) so the group is open on first paint
  if (seenNav !== activeNav) {
    setSeenNav(activeNav)
    if (activeGroup && !openGroups.has(activeGroup)) setOpenGroups(new Set(openGroups).add(activeGroup))
  }

  const toggleGroup = (id) => setOpenGroups(s => {
    const next = new Set(s)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })

  return { openGroups, toggleGroup }
}

